import { IconButton, Stack, Typography } from '@mui/material';
import { useTheme } from "@mui/material/styles";
import RowTypography from 'components/Table/RowTypography';
import React, { useEffect, useState } from 'react';
import { ReactComponent as ClockIcon } from '../../assets/time-history.svg';
import CloseIcon from '@mui/icons-material/Close';
import { CustomizedDataGrid } from 'components/customized_data_grid';
import LoaderComponent from 'components/Loader';
import { getSLAHistory } from 'service/service';


interface SLAHistoryProps {
	setSlaHistory: any;
	proInfo: any;
}

function formatTime(number: number) {
	let str = number?.toString().padStart(4, "0"); // Ensure it's 4 digits (e.g., "0115")
	let hours = str?.slice(0, 2); // Get the first two digits as hours
	let minutes = str?.slice(2, 4); // Get the last two digits as minutes
	return hours || minutes ? `${hours}:${minutes}` : 'NA';
}

const getRowDisplayValue = (row: any, columnKey: string): string => {
	if (columnKey === 'SLA_Type') {
		if (row[columnKey] === 0) return 'Daily';
		if (row[columnKey] === 1) return 'Weekly';
		return '--';
	}

	if (columnKey === 'SLA_Time') {
		if (!row[columnKey]) return '--';
		const timeStr = String(row[columnKey]).padStart(4, '0');
		return `${timeStr.slice(0, 2)}:${timeStr.slice(2)}`;
	}

	if (columnKey === 'Updated_At') {
		if (row.Updated_At) return row.Updated_At.split('T')[0];
		if (row.Created_At) return row.Created_At.split('T')[0];
		return '--';
	}

	return row[columnKey] || '--';
};

const ColumnFormatter = () => ({ row, column }: any) => {
	return (
		<Stack direction="row" justifyContent="space-between" alignItems="center">
			<RowTypography>{getRowDisplayValue(row, column.key)}</RowTypography>
		</Stack>
	);
};

const SLAHistory: React.FC<SLAHistoryProps> = ({ setSlaHistory, proInfo }: any) => {
	const defaultColumnNames = [
		'SLA Type',
		'Days',
		'SLA Time (CST)',
		'SLA Date (Sch.Date+)',
		'Update Date',
		'Created By'
	];

	const columnNames = [
		{ name: 'SLA_Type', label: 'SLA Type' },
		{ name: 'SLA_Week_Days', label: 'Days' },
		{ name: 'SLA_Time', label: 'SLA Time (CST)' },
		{ name: 'SLA_Date', label: 'SLA Date (Sch.Date+)' },
		{ name: 'Updated_At', label: 'Update Date' },
		{ name: 'Updated_By', label: 'Created By' }


	];

	const theme = useTheme();
	const colors = theme.palette;
	const [isSlaHistoryData, setSlaHistoryData] = useState<any[]>([]);
	const [loading, setLoading] = useState<boolean>(false);

	async function SlaHistoryFunc() {
		setLoading(true);
		const res = await getSLAHistory(proInfo.hisProId);
		if (res.success) {
			setLoading(false);
			setSlaHistoryData(res.data);

		}
	}

	//fetching data
	useEffect(() => {
		SlaHistoryFunc();
	}, [proInfo]);

	function getColumns(): Array<any> {
		const formatter = ColumnFormatter();
		return columnNames.map((col: any) => ({
			key: col.name,
			name: col.label,
			width: 'auto',
			flexGrow: 1,
			formatter
		}));
	}
	
	let scheduleSuffix = '';
	if (proInfo.hisSlaDat && proInfo.hisSlaDate === 1) {
		scheduleSuffix = '+ 1';
	}

	return (
		<Stack direction="column" p={2} position='relative' sx={{ bgcolor: colors.backgroundLight.primary}}>
			<IconButton
				onClick={() => setSlaHistory(false)}
				sx={{
					position: 'absolute',
					top: 8,
					right: 8,
					zIndex: 1
				}}
			>
				<CloseIcon sx={{ color: `${colors.buttons.primary.solidBg}` }}/>
			</IconButton>
			<Stack
				direction="row"
				spacing={12}
				pt={1}
				pb={2}
				marginBottom={1}
				borderBottom={`0.5px solid ${colors.table.text}`}
			>
				<Stack>
					<Typography sx={{ color: colors.table.text }}>
						Project Name&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
						<Typography component="span" sx={{ fontWeight: 'bold' }}>
							{proInfo.hisProName}
						</Typography>
					</Typography>
				</Stack>

				<Stack>
					<Typography sx={{ color: colors.table.text }}>
						Current SLA Time &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
						<Typography component="span" sx={{ fontWeight: 'bold' }}>
							{formatTime(proInfo.hisSlaTime)}
						</Typography>
					</Typography>
				</Stack>
				<Stack>
					<Typography sx={{ color: colors.table.text }}>
						Current SLA Date &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
						<Typography component="span" sx={{ fontWeight: 'bold' }}>
							{`Schedule Date ${scheduleSuffix}`}
						</Typography>
					</Typography>
				</Stack>
			</Stack>
			{loading ? (
				<LoaderComponent />
			) : (
				<CustomizedDataGrid
					title={
						<Stack direction="row" alignItems="center" spacing={1} mt="1rem">
							<ClockIcon style={{ color: 'red' }} />
							<Typography fontSize="16px">Past SLA Configurations</Typography>
						</Stack>
					}
					tableColumns={getColumns()}
					tableData={isSlaHistoryData}
					tableType="history-list"
					defaultColumnNames={defaultColumnNames}
					autoSetDraggedColumns={false}
					listLength={isSlaHistoryData.length}
					showExport={false}
					isDateRangeFilter={false}
					isDisabled={false}
					isRenderPagination={false}
					isShowMoreButton={false}
					style={{ maxHeight: '250px' }}
					rowHeight={40}
				/>
			)}
		</Stack>
	);
};

export default SLAHistory;
